import type { ChangeEventHandler, KeyboardEventHandler } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { AvatarEditor } from '@frameio/shared/components/AvatarEditor';
import { useIsMatchingClientName } from '@frameio/shared/contexts/ClientNameContext';
import { TextField, theme, useDebounceCallback } from '@frameio/vapor';
import { DefaultAvatar } from './DefaultAvatar';
import TEXT from './generated/TEXT/DetailsInput';

type DetailsInputProps = {
  name: string;
  avatarBlob: Blob | undefined;
  avatarAlt: string;
  onNameChange: (name: string) => void;
  onAvatarChange: (blob: Blob) => void;
  onSubmit: () => void;
};

export const DetailsInput = ({
  name,
  avatarBlob,
  avatarAlt,
  onNameChange,
  onAvatarChange,
  onSubmit,
}: DetailsInputProps) => {
  const nameInputRef = useRef<HTMLInputElement | null>(null);
  const isWebClient = useIsMatchingClientName('web');
  const isSaveDisabled = name.trim() === '';
  const debouncedAvatarAlt = useDebouncedAvatarAlt(avatarAlt);
  const avatarThumb = useMemo(() => {
    if (avatarBlob instanceof Blob) {
      return URL.createObjectURL(avatarBlob);
    }
    return null;
  }, [avatarBlob]);

  const handleNameKeyDown: KeyboardEventHandler<HTMLInputElement> = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      if (!isSaveDisabled) {
        onSubmit();
      }
    }
  };

  const handleNameChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    onNameChange(event.target.value);
  };

  return (
    <NameContainer>
      {isWebClient && (
        <AvatarContainer>
          <AvatarEditor
            avatarAlt={debouncedAvatarAlt}
            avatarOriginal={avatarThumb}
            avatarSize={AVATAR_SIZE}
            avatarThumb={avatarThumb}
            editLabel={TEXT.editAvatar}
            FallbackAnonymousIconComponent={DefaultAvatar}
            onSave={onAvatarChange}
            shape="square"
          />
        </AvatarContainer>
      )}
      <NameInputContainer>
        <TextField
          ref={nameInputRef}
          data-testid="workspace-details-input-name"
          errorVariant="inline"
          label={TEXT.workspaceName}
          maxLength={INPUT_CHAR_LIMIT}
          onChange={handleNameChange}
          onKeyDown={handleNameKeyDown}
          placeholder={TEXT.addWorkspaceName}
          value={name}
        />
      </NameInputContainer>
    </NameContainer>
  );
};

// Avatar color is hashed from the alt, so a live value changes color on every keystroke.
function useDebouncedAvatarAlt(avatarAlt: string) {
  const [debouncedAvatarAlt, setDebouncedAvatarAlt] = useState(avatarAlt);
  const [updateAvatarAlt] = useDebounceCallback(
    setDebouncedAvatarAlt,
    AVATAR_ALT_DEBOUNCE_MS
  );
  useEffect(() => {
    updateAvatarAlt(avatarAlt);
  }, [avatarAlt, updateAvatarAlt]);
  return debouncedAvatarAlt;
}

TEXT.DEFINE({
  /** Placeholder text for workspace name input */
  addWorkspaceName: 'Add workspace name',
  /** Label for the avatar editor action */
  editAvatar: 'Edit workspace avatar',
  /** Label for the workspace name field */
  workspaceName: 'Workspace name',
});

const AVATAR_SIZE = 48;
const AVATAR_ALT_DEBOUNCE_MS = 300;
const INPUT_CHAR_LIMIT = 255;

export const AvatarContainer = styled.div`
  padding-right: ${theme.spaces[16]};
  padding-top: ${theme.spaces[4]};
`;

export const NameContainer = styled.div`
  align-items: center;
  display: flex;
  padding: ${theme.spaces[16]} 0;
  width: 100%;
`;

export const NameInputContainer = styled.div`
  flex-grow: 1;
`;
